import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import Form from 'react-bootstrap/Form'
import {InputGroup} from "react-bootstrap";
import Button from "react-bootstrap/Button";
import PlayerPickingTable from "../players/tables/PlayerPickingTable";
import PlayerSeedingEditableTable from "../players/tables/PlayerSeedingEditableTable";
import {setPlayerElo} from "../../util/HelperUtils";
import NewPlayerModal from "../players/modals/NewPlayerModal";
import {useAllPlayersForOrganizer} from "../../hooks/queries/usePlayer";
import {useCreatePlayer} from "../../hooks/mutations/usePlayerMutations";
import {useSetTournamentPlayers, useStartTournament} from "../../hooks/mutations/useTournamentMutations";
import type {Player, TournamentDetail} from "../../types/domain";

interface PlayerPickerProps {
  tournament: TournamentDetail;
}

function PlayerPicker(props: PlayerPickerProps) {
  const [ pickedPlayers, setPickedPlayers ] = useState<Player[]>([])
  const [ showAddPlayerForm, setShowAddPlayerForm ] = useState(false)
  const [ numberOfGroups, setNumberOfGroups ] = useState<number | null>(null);
  const [ filter, setFilter ] = useState("")
  const { t } = useTranslation();

  const ladderId = props.tournament?.ladder?.id;
  const { data: players = [] } = useAllPlayersForOrganizer(ladderId);
  const createPlayerMutation = useCreatePlayer();
  const setTournamentPlayersMutation = useSetTournamentPlayers();
  const startTournamentMutation = useStartTournament();

  const filteredPlayers = useMemo(
    () => players.filter(p => p.filter?.includes(filter.toUpperCase()) ?? true),
    [players, filter]
  )

  // Seed local state from props ONCE per tournament. Subsequent refetches (triggered by our
  // own auto-save) must NOT overwrite the user's in-flight edits.
  const initializedForTournament = useRef<number | null>(null);
  // ID order known to be persisted on the server. Auto-save fires when local diverges.
  const lastSyncedIds = useRef<string>("");

  useEffect(() => {
    const t = props.tournament;
    if (t == null || t.players == null) return;
    if (initializedForTournament.current === t.id) return;
    initializedForTournament.current = t.id;
    const players = t.players.map(seededPlayer => seededPlayer.player);
    lastSyncedIds.current = players.map(p => p.id).join(",");
    setPickedPlayers(players);
  }, [props.tournament])

  // Debounced auto-save: persist whenever the local order/composition diverges from server.
  useEffect(() => {
    if (initializedForTournament.current !== props.tournament?.id) return;
    const currentIds = pickedPlayers.map(p => p.id).join(",");
    if (currentIds === lastSyncedIds.current) return;
    const handle = setTimeout(async () => {
      try {
        await setTournamentPlayersMutation.mutateAsync({
          tournamentId: props.tournament.id,
          playerIds: pickedPlayers.map(p => p.id),
        });
        lastSyncedIds.current = currentIds;
      } catch (_) {
        // Save failed — leave lastSyncedIds untouched so the next change retries.
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [pickedPlayers, props.tournament?.id, setTournamentPlayersMutation])

  const updateSeeding = (previousIndex: number, newIndex: number) => {
    if (newIndex >= 0 && newIndex < pickedPlayers.length) {
      const newPickedPlayers = [...pickedPlayers]
      const [updatedPlayer] = newPickedPlayers.splice(previousIndex, 1);
      newPickedPlayers.splice(newIndex, 0, updatedPlayer);
      setPickedPlayers(newPickedPlayers)
    }
  }

  const removePlayer = (index: number) => {
    if (index >= 0 && index < pickedPlayers.length) {
      const newPickedPlayers = [...pickedPlayers]
      newPickedPlayers.splice(index, 1)
      setPickedPlayers(newPickedPlayers)
    }
  }

  const sortByElo = useCallback(() => {
    const newPickedPlayers = [...pickedPlayers]
    newPickedPlayers.sort((a, b) => (a.elo ?? 0) < (b.elo ?? 0) ? 1 : -1)
    setPickedPlayers(newPickedPlayers)
  }, [pickedPlayers, setPickedPlayers])

  const startTournament = useCallback(async (tournamentId: number) => {
    await setTournamentPlayersMutation.mutateAsync({
      tournamentId,
      playerIds: pickedPlayers.map(p => p.id),
    });
    await startTournamentMutation.mutateAsync({ tournamentId, numberOfGroups });
  }, [pickedPlayers, numberOfGroups, setTournamentPlayersMutation, startTournamentMutation])

  const selectPlayer = useCallback((player: Player) => {
    if (pickedPlayers.filter(p => p.id === player.id).length === 0) {
      setPickedPlayers([...pickedPlayers, player])
    }
  }, [pickedPlayers, setPickedPlayers])

  const handleNewPlayer = useCallback(async (player: Partial<Player>) => {
    const created = await createPlayerMutation.mutateAsync(player);
    setPlayerElo(created, props.tournament.ladder.id)
    selectPlayer(created)
    setShowAddPlayerForm(false)
  }, [selectPlayer, setShowAddPlayerForm, createPlayerMutation, props.tournament.ladder.id])

  const startTournamentButtonDisabled = () => {
    if (props.tournament.type === "GROUPS" || props.tournament.type === "GROUPS_PLAYOFF") {
      return numberOfGroups == null || numberOfGroups < 1 || pickedPlayers.length < numberOfGroups;
    }
    return false;
  }

  return (
    <div>
      <div className="left-half-responsive">
        <div className="header-with-buttons mb-3">
          <h3>{t("seeding")}</h3>
          {props.tournament.canManage ?
            <Button variant="outline-info" onClick={() => sortByElo()}>
              {t("sortByElo")}
            </Button> : null }
          {props.tournament.canManage ?
            <Button variant="success" onClick={() => startTournament(props.tournament.id)}
                    disabled={startTournamentButtonDisabled()}>
              {t("startTournament")}
            </Button> : null }
        </div>
        {props.tournament.canManage && (props.tournament.type === "GROUPS" || props.tournament.type === "GROUPS_PLAYOFF") ?
          <div className="d-flex justify-content-end mb-3">
            <InputGroup>
              <InputGroup.Text id="groupBar">{t("numberOfGroups")}</InputGroup.Text>
              <Form.Control type="number" min="1" onChange={e => setNumberOfGroups(e.target.value ? Number(e.target.value) : null)} />
            </InputGroup>
          </div> : null }
        <PlayerSeedingEditableTable players={pickedPlayers} updateSeeding={updateSeeding} removePlayer={removePlayer} />
      </div>
      <div className="right-half-responsive">
        <div className="header-with-buttons mb-3">
          <h3>{t("players")}</h3>
          {props.tournament.canManage ?
            <Button variant="success" onClick={() => setShowAddPlayerForm(true)} className="table-button me-3">
              {t("createPlayer")}
            </Button>
            : null }
          <InputGroup style={{width: "24rem"}}>
            <InputGroup.Text id="searchbar">{t("search")}</InputGroup.Text>
            <Form.Control value={filter} onChange={e => setFilter(e.target.value)} />
          </InputGroup>
        </div>
        <PlayerPickingTable players={filteredPlayers} selectPlayer={selectPlayer} />
      </div>
      <NewPlayerModal isOpen={showAddPlayerForm} closeModal={() => setShowAddPlayerForm(false)} handleSubmit={handleNewPlayer} />
    </div>
  );
}

export default PlayerPicker
